import { defineComponent as _defineComponent } from 'vue'
import { computed, ref, watch, onMounted, nextTick } from 'vue'

  import LoadingSpinner from '@/components/common/LoadingSpinner.vue'
  import SVGRenderer from '@/components/common/SVGRenderer.vue'
  import KetchUpButton from '@/components/common/KetchUpButton.vue'
  import Choice from '@/components/common/Choice.vue'
  import LastActivity from '@/components/common/LastActivity.vue'
  import CancelInviteNotification from '@/components/user-management/CancelInviteNotification.vue'
  import BarChart from '@/components/common/BarChart.vue'

  import useIcons from '@/composables/useIcons'
  import useCommonMixin from '@/composables/useCommonMixin'
  import useSegment from '@/composables/useSegment'
  import useCourse from '@/composables/useCourse'
  import useI18n from '@/composables/useI18n'

  import { UserModule } from '@/store/modules/user'
  import { CourseModule } from '@/store/modules/course'

  import UsersApi from '@/services/api/UsersApi'

  import type { PropType } from 'vue'
  import type { UserMember, UserRole } from '@/services/interfaces/Auth0'

  import eventBus from '@/main'
  import { DateTime } from 'luxon'

  
export default /*#__PURE__*/_defineComponent({
  __name: 'UserPreviewDetails',
  props: {
    user: {
      type: Object as PropType<UserMember>,
      required: true,
    },
  },
  emits: ['delete-user', 'create-new-group', 'reload-user-members'],
  setup(__props, { emit }) {

const props = __props;

  

  

  const { checkboxSquare, checkboxSquareChecked, arrowRight, eclipseLoaderIcon } = useIcons()
  const { displayUserRole, openAssignCoursesModal, isDevCompany, getColorVariableValue } = useCommonMixin()
  const { companyUsersGroupsAssigned } = useSegment()
  const { translateString } = useI18n()
  const { courseBundlesOverview } = useCourse()

  const selectedRoles = ref([] as { roleId: string; _destroy?: boolean }[])
  const selectedGroupIds = ref([] as string[])
  const intentToCancelInvite = ref(false)
  const changingUserRoles = ref(false)
  const changingUserGroups = ref(false)
  const resendingUserInvite = ref(false)
  const cancellingUserInvite = ref(false)
  const deleteCompanyUserMessage = ref('')
  const rolesAndGroups = ref(null as HTMLDivElement | null)
  const rolesAndGroupsHeight = ref('0px')

  const userRoles = computed(() => UserModule.userRoles)

  const userGroups = computed(() => UserModule.userGroups?.filter((g) => g.name !== 'All Users') || [])

  const userHasRole = computed(() => {
    return (roleId?: string) => {
      return selectedRoles.value.some((r) => r.roleId === roleId)
    }
  })

  const userBelongsToGroup = computed(() => {
    return (groupId: string) => {
      return selectedGroupIds.value.includes(groupId)
    }
  })

  const disableChoice = computed(() => {
    return (role: string) => {
      return UserModule.email === props.user.email && role === 'COMPANY_ADMIN'
    }
  })

  const roleChoiceIcon = computed(() => {
    return (roleId?: string) => {
      const svgProps: any = {
        strokeColor: 'var(--primary-text-color)',
        icon: checkboxSquare.value,
      }
      if (selectedRoles.value.some((r) => r.roleId === roleId && !r._destroy)) {
        svgProps.icon = checkboxSquareChecked.value
      }
      return svgProps
    }
  })

  const groupChoiceIcon = computed(() => {
    return (groupId: string) => {
      const svgProps: any = {
        strokeColor: 'var(--primary-text-color)',
        icon: checkboxSquare.value,
      }
      if (userBelongsToGroup.value(groupId)) {
        svgProps.icon = checkboxSquareChecked.value
      }
      return svgProps
    }
  })

  const purchasedMemberCourses = computed(() => {
    if (CourseModule.memberCourses === null) return ''
    return CourseModule.purchasedMemberCourses.length
  })

  const userId = computed(() => props.user.id)
  CourseModule.getMemberCourses({
    auth0Id: userId.value,
    reset: true,
    filterDevCourseBundles: isDevCompany.value,
  })

  const courseActivities = computed(() => UserModule.userCourseActivities)

  const examAppointments = computed(() => props.user.examAppointments)

  const appointmentCourseName = computed(() => (courseId: string) => {
    const course = courseBundlesOverview.value
      ?.flatMap((bundle) => bundle.courses)
      .find((course) => course.id === courseId)
    return course ? (course.shortTitle ? course.shortTitle : course.title) : ''
  })

  const userSessionsChartLabels = computed(() => {
    const allDates = props.user?.sessions.map((session) => session.date)
    const uniqueDates = [...new Set(allDates)].slice(0, 7)
    const currentDate = DateTime.local().startOf('day')

    // Create an array of the past 7 dates
    const past7Dates = []
    for (let i = 0; i < 7; i++) {
      const date = currentDate.minus({ days: i }).toISODate()
      past7Dates.push(date)
    }

    // Prefill the missing dates
    const filledDates = past7Dates.filter((date) => !uniqueDates.includes(date!)).concat(uniqueDates)
    // Order the result in ascending order
    return filledDates.sort((a, b) => {
      if (a && b) {
        const aDate = DateTime.fromISO(a)
        const bDate = DateTime.fromISO(b)

        return aDate.toMillis() - bDate.toMillis()
      }

      return 0
    })
  })

  const userSessionsChartData = computed(() => {
    const combinedData: { [key: string]: number } = {}

    // Combine data from the same date
    props.user.sessions.forEach((session) => {
      const { date, startTime, endTime } = session

      if (!combinedData[`${date}`]) {
        combinedData[`${date}`] = 0
      }

      const sessionStart = DateTime.fromISO(startTime)
      const sessionEnd = DateTime.fromISO(endTime)
      const sessionDuration = sessionEnd.diff(sessionStart, 'minutes').minutes

      combinedData[`${date}`] += sessionDuration
    })

    const orderedData = userSessionsChartLabels.value.map((date) => (date ? combinedData[date] : 0))

    return orderedData.slice(0, 7)
  })

  const chartData = computed(() => {
    return {
      labels: userSessionsChartLabels.value,
      datasets: [
        {
          label: translateString('homepage.timeSpentMinutes'),
          backgroundColor: [getColorVariableValue.value('--primary-color')],
          data: userSessionsChartData.value,
        },
      ],
    }
  })

  const updateUserRole = () => {
    changingUserRoles.value = true
    const roles = selectedRoles.value.map((r) => {
      const role: UserRole = {
        roleId: r.roleId,
        companyId: UserModule.currentCompany!.id,
        name: UserModule.userRoles.find((role) => role.id === r.roleId)?.name ?? '',
      }
      const existingRole = '_destroy' in r
      if (existingRole) {
        role.id = props.user.roles.find((role) => role.roleId === r.roleId)?.id
        role._destroy = r._destroy
      }
      return role
    })
    UsersApi.updateUserRole(UserModule.currentCompany!.id, props.user.id, roles)
      .then((user) => {
        UserModule.appendUserMembers({ users: [user], action: 'update-roles', roles: roles })
        eventBus.$toasted.success(translateString('homepage.roleUpdateMessage'))
        emit('reload-user-members')
      })
      .finally(() => (changingUserRoles.value = false))
  }

  const assignGroupToUser = () => {
    UsersApi.assignUsersToGroup(UserModule.currentCompany!.id, [props.user.email], selectedGroupIds.value).then(
      (resp) => {
        changingUserGroups.value = false
        companyUsersGroupsAssigned(UserModule.currentCompany!.id, [props.user.email], selectedGroupIds.value)
        eventBus.$toasted.success(translateString('homepage.assignedGroupSuccessfully'))

        const currentlyAssignedGroupIds = resp.users[0].groups.map((group) => group.id)
        UserModule.appendUserMembers({
          users: resp.users,
          action: 'update-groups',
          groupIds: currentlyAssignedGroupIds,
        })

        CourseModule.getCourseSections(UserModule.currentCompany!.id)
        UserModule.getCompanyActivityLog()
        emit('reload-user-members')
      },
    )
  }

  const unAssignUserFromGroup = (groupId: string) => {
    UsersApi.unAssignUsersToGroup(UserModule.currentCompany!.id, {
      emails: [props.user.email],
      groupIds: [groupId],
    }).then((resp) => {
      changingUserGroups.value = false
      eventBus.$toasted.success(translateString('homepage.unAssignedGroupSuccessfully'))

      const currentlyAssignedGroupIds = resp.users[0].groups.map((group) => group.id)
      UserModule.appendUserMembers({
        users: resp.users,
        action: 'update-groups',
        groupIds: currentlyAssignedGroupIds,
      })

      CourseModule.getCourseSections(UserModule.currentCompany!.id)
      UserModule.getCompanyActivityLog()
      emit('reload-user-members')
    })
  }

  const onRoleSelection = (role: UserRole) => {
    if (disableChoice.value(role.name)) return
    if (role.id) {
      if (userHasRole.value(role.id)) {
        const index = selectedRoles.value.findIndex((r) => r.roleId === role.id)
        if ('_destroy' in selectedRoles.value[index]) {
          selectedRoles.value[index]._destroy = !selectedRoles.value[index]._destroy
        } else {
          selectedRoles.value.splice(index, 1)
        }
      } else {
        selectedRoles.value.push({ roleId: role.id })
      }
    }
    updateUserRole()
  }

  const onGroupSelection = (groupId: string) => {
    // assign or un-assign user to group
    changingUserGroups.value = true
    if (userBelongsToGroup.value(groupId)) {
      const index = selectedGroupIds.value.indexOf(groupId)
      selectedGroupIds.value.splice(index, 1)
      unAssignUserFromGroup(groupId)
    } else {
      selectedGroupIds.value.push(groupId)
      assignGroupToUser()
    }
  }

  const resendUserInvite = () => {
    resendingUserInvite.value = true
    UsersApi.inviteUser({
      companyId: UserModule.currentCompany!.id,
      emails: [props.user.email],
      reInviteUsers: true,
    })
      .then(() => {
        eventBus.$toasted.success(translateString('homepage.inviteUser.inviteResentSuccessfully'))
      })
      .finally(() => (resendingUserInvite.value = false))
  }

  const cancelUserInvite = () => {
    const rolesUserHas = selectedRoles.value.map((role) => role.roleId)
    cancellingUserInvite.value = true
    UsersApi.deleteCompanyUsers(UserModule.currentCompany!.id, { emails: [props.user.email] })
      .then(() => {
        emit('delete-user', { userId: props.user.id, rolesUserHas: rolesUserHas })
        eventBus.$toasted.success(translateString('homepage.inviteUser.userRemovedSuccessfully'))
        intentToCancelInvite.value = false
      })
      .catch((error) => {
        if (error.response?.status === 400 && error.response?.data?.response === "Couldn't find User") {
          emit('delete-user', { userId: props.user.id, rolesUserHas: rolesUserHas })
          eventBus.$toasted.success(translateString('homepage.inviteUser.userRemovedSuccessfully'))
          intentToCancelInvite.value = false
        }
      })
      .finally(() => {
        cancellingUserInvite.value = false
      })
  }

  const triggerDeleteCompanyUser = () => {
    intentToCancelInvite.value = true
    deleteCompanyUserMessage.value = translateString('homepage.deleteUserMessage')
  }

  const setSelectedRoles = () => {
    selectedRoles.value = []
    props.user.roles.forEach((role) => {
      if (role.name !== 'ADMIN') {
        selectedRoles.value.push({
          roleId: role.roleId,
          _destroy: false,
        })
      }
    })
  }

  const setSelectedGroups = () => {
    props.user.groups.forEach((group) => {
      selectedGroupIds.value.push(group.id)
    })
  }

  const getUserCourseActivity = () => {
    if (!userId.value) {
      UserModule.setUserCourseActivities({ activities: [] })
      return
    }
    UserModule.setUserCourseActivities({ userId: userId.value })
  }

  const updateRolesAndGroupsHeight = () => {
    if (rolesAndGroups.value) {
      rolesAndGroupsHeight.value = `${rolesAndGroups.value.scrollHeight}px`
    }
  }

  onMounted(() => {
    updateRolesAndGroupsHeight()
  })

  watch(
    () => props.user,
    () => {
      nextTick(() => {
        updateRolesAndGroupsHeight()
      })
    },
    { deep: true },
  )

  watch(
    () => selectedRoles.value,
    () => {
      nextTick(() => {
        updateRolesAndGroupsHeight()
      })
    },
    { deep: true },
  )

  watch(
    () => selectedGroupIds.value,
    () => {
      nextTick(() => {
        updateRolesAndGroupsHeight()
      })
    },
    { deep: true },
  )

  setSelectedRoles()
  setSelectedGroups()
  getUserCourseActivity()

  watch(userId, () => {
    getUserCourseActivity()
  })

  watch(
    () => props.user,
    () => {
      setSelectedRoles()
    },
  )

return { __sfc: true,props, emit, checkboxSquare, checkboxSquareChecked, arrowRight, eclipseLoaderIcon, displayUserRole, openAssignCoursesModal, isDevCompany, getColorVariableValue, companyUsersGroupsAssigned, translateString, courseBundlesOverview, selectedRoles, selectedGroupIds, intentToCancelInvite, changingUserRoles, changingUserGroups, resendingUserInvite, cancellingUserInvite, deleteCompanyUserMessage, rolesAndGroups, rolesAndGroupsHeight, userRoles, userGroups, userHasRole, userBelongsToGroup, disableChoice, roleChoiceIcon, groupChoiceIcon, purchasedMemberCourses, userId, courseActivities, examAppointments, appointmentCourseName, userSessionsChartLabels, userSessionsChartData, chartData, updateUserRole, assignGroupToUser, unAssignUserFromGroup, onRoleSelection, onGroupSelection, resendUserInvite, cancelUserInvite, triggerDeleteCompanyUser, setSelectedRoles, setSelectedGroups, getUserCourseActivity, updateRolesAndGroupsHeight, computed, ref, watch, onMounted, nextTick, LoadingSpinner, SVGRenderer, KetchUpButton, Choice, LastActivity, CancelInviteNotification, BarChart, useIcons, useCommonMixin, useSegment, useCourse, useI18n, UserModule, CourseModule, UsersApi, eventBus, DateTime }
}

})